import { requestApi } from "../api/client.js";

export interface TrainingModule {
  content_md: string | null;
  id: string;
  org_id: string | null;
  plan_card_code: string | null;
  plan_pack_id: string | null;
  renewal_months: number | null;
  sort_order: number;
  status: "active" | "retired";
  title: string;
}

export interface TrainingCompletion {
  competency_note: string | null;
  completed_on: string;
  expires_on: string | null;
  id: string;
  record_id: string | null;
  trainee_acknowledged_at: string | null;
  trainer_acknowledged_at: string | null;
  trainer_staff_profile_id: string | null;
}

export interface TrainingAssignment {
  assigned_by: string | null;
  completion: TrainingCompletion | null;
  due_on: string | null;
  id: string;
  location_id: string | null;
  module: TrainingModule;
  staff_profile_id: string;
  status: "assigned" | "cancelled" | "completed" | "in_progress";
  training_module_id: string;
}

export function listTrainingModules(): Promise<TrainingModule[]> {
  return requestApi("/training/modules");
}

export function createTrainingModule(input: {
  content_md?: string | null;
  renewal_months?: number | null;
  title: string;
}): Promise<TrainingModule> {
  return requestApi("/training/modules", {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export function listTrainingAssignments(): Promise<TrainingAssignment[]> {
  return requestApi("/training/assignments");
}

export function createTrainingAssignments(input: {
  due_on?: string | null;
  location_id: string;
  staff_profile_ids: string[];
  training_module_id: string;
}): Promise<TrainingAssignment[]> {
  return requestApi("/training/assignments", {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export function completeTrainingAssignment(
  id: string,
): Promise<TrainingAssignment> {
  return requestApi(`/training/assignments/${id}/complete`, {
    body: JSON.stringify({
      completed_on: new Date().toISOString().slice(0, 10),
    }),
    method: "POST",
  });
}

export function signOffTrainingCompletion(
  id: string,
  competencyNote?: string,
): Promise<TrainingAssignment> {
  return requestApi(`/training/completions/${id}/sign-off`, {
    body: JSON.stringify({ competency_note: competencyNote?.trim() || null }),
    method: "POST",
  });
}

export type TrainingDisplayState =
  | "assigned"
  | "awaiting_sign_off"
  | "cancelled"
  | "complete"
  | "expired"
  | "expires_soon";

export function trainingDisplayState(
  assignment: TrainingAssignment,
  today = new Date(),
): TrainingDisplayState {
  if (assignment.status === "cancelled") return "cancelled";
  if (!assignment.completion) return "assigned";
  if (!assignment.completion.trainer_acknowledged_at)
    return "awaiting_sign_off";
  if (!assignment.completion.expires_on) return "complete";
  const expiry = new Date(`${assignment.completion.expires_on}T23:59:59`);
  if (expiry.getTime() < today.getTime()) return "expired";
  if (expiry.getTime() - today.getTime() <= 30 * 86_400_000)
    return "expires_soon";
  return "complete";
}

export function trainingStateLabel(state: TrainingDisplayState): string {
  return {
    assigned: "Assigned",
    awaiting_sign_off: "Awaiting sign-off",
    cancelled: "Cancelled",
    complete: "Complete",
    expired: "Expired",
    expires_soon: "Expires soon",
  }[state];
}
